/// <reference types="vite/client" />
import type { ReactNode } from 'react';
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRoute,
  useRouterState,
} from '@tanstack/react-router';
import { analyticsHeadScripts, dubHeadScripts } from '../lib/analytics.js';
import { jsonLd, organisationSchema, seo } from '../lib/seo.js';
import { SiteShell } from '../components/site/SiteShell.js';
import { NotFound } from '../components/site/NotFound.js';
import { site } from '../lib/site.js';
import appCss from '../styles/app.css?url';

/**
 * The document every marketing page is rendered into.
 *
 * The defaults here are the ones a page has to *override* rather than repeat,
 * so a route that forgets its metadata still ships something true rather than
 * an empty `<title>`.
 *
 * Every default except one. `meta` merges by `name`/`property`, so a page's
 * title and description genuinely replace these; `links` merge by
 * concatenation, so a canonical here would be *added* to the page's rather
 * than replaced by it — two canonicals per page, the first pointing at `/`.
 * Hence `canonical: false`. See the option's note in `lib/seo.ts`.
 */
export const Route = createRootRoute({
  head: () => {
    const { meta, links } = seo({
      title: 'ReviewsJet',
      description:
        'Bring your reviews together, manage them in one place, and display your best ones anywhere on your website.',
      path: '/',
      canonical: false,
    });

    return {
      meta: [
        { charSet: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        { name: 'color-scheme', content: 'light' },
        { name: 'theme-color', content: '#ffffff' },
        ...meta,
      ],
      links: [
        { rel: 'stylesheet', href: appCss },
        { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
        ...links,
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(organisationSchema()),
        },
        // GA4 and the Ads tag — production only, see `lib/analytics.ts`.
        ...analyticsHeadScripts(),
        // Dub's script, when the partner programme's domain is configured.
        ...dubHeadScripts(new URL(site.url).hostname),
      ],
    };
  },
  shellComponent: RootDocument,
  /*
    The header and footer sit on every page, so they belong to the root route
    rather than being repeated per route — the version where each page renders
    its own shell is the version where one page eventually renders a slightly
    different one.
  */
  component: RootRoute,
  /*
    Just the body. The root component still wraps the outlet in the shell, and
    a not-found from a child (a missing blog post, an unknown widget slug) is
    rendered *in* that outlet. Wrapping again here was two headers stacked on
    every miss.
  */
  notFoundComponent: NotFound,
});

/**
 * The shell, except where a page is meant to be framed.
 *
 * Two branches render without it, for the same underlying reason — the visitor
 * is not here to browse this site.
 *
 * `/preview/*` renders a bare widget for an `<iframe>`; a header and footer
 * inside a 375px frame would be the thing being measured rather than the
 * widget.
 *
 * `/f/*` is a business's own review form, opened by *their* customer from an
 * email or a receipt. A "Pricing" link and a "Get started free" button on that
 * page are an advert placed in the middle of somebody else's conversation with
 * their customer — and the customer, who has never heard of ReviewsJet, has
 * nowhere on this site they were trying to go.
 *
 * A prefix check rather than a flag on each route: the rule is about whole
 * branches of the site, and stating it once is how it stays true of the next
 * route added under either.
 */
const BARE = ['/preview/', '/f/'];

function RootRoute() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  if (BARE.some((prefix) => pathname.startsWith(prefix))) return <Outlet />;

  return (
    <SiteShell>
      <Outlet />
    </SiteShell>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {/*
          The first thing in the tab order, and visible only once it has focus.
          Every page here is mostly navigation and repeated sections, so a
          keyboard visitor arriving from search would otherwise tab through the
          whole header on every page to reach the thing they came for.
        */}
        <a
          href="#main"
          className="sr-only rounded-md bg-ink-950 px-4 py-2 text-white focus-visible:not-sr-only focus-visible:absolute focus-visible:top-3 focus-visible:left-3 focus-visible:z-50"
        >
          Skip to content
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}
